import React from 'react';
import { useParams } from 'react-router';
import {
  CondensedHeader,
  Gutter,
  Layout,
  LayoutItem,
  MatchChecklist,
  MatchChecklistItem,
  SectionHeader,
  useApi,
  useBreakpoint,
  Well,
} from '@axiom/ui';
import type {
  AfcSubmission,
  AfcSubmissionCandidate,
  CandidateLegalTechSkill,
  Taxonomy,
} from '@axiom/validation';

import { AccountIntakeApi } from '../../api/account-intake-api';

import { EngagementTalentMatchChecklistUtil } from './EngagementTalentMatchChecklistUtil';

export type EngagementTalentMatchChecklistProps = {
  candidate: AfcSubmissionCandidate;
  legalTechSkills: CandidateLegalTechSkill[];
  submission: AfcSubmission;
  taxonomy: Taxonomy;
};

export const EngagementTalentMatchChecklist = ({
  candidate,
  legalTechSkills,
  submission,
  taxonomy,
}: EngagementTalentMatchChecklistProps) => {
  const { accountId, submissionId } = useParams();
  const [{ data: intakeData }] = useApi(
    AccountIntakeApi.readAccountIntake(
      accountId ?? '',
      submissionId ? { submissionId } : undefined
    )
  );

  const { isSmallScreen } = useBreakpoint();

  if (!intakeData) {
    return null;
  }

  const checklistCategories =
    EngagementTalentMatchChecklistUtil.generateChecklist({
      candidate,
      intakeData,
      legalTechSkills,
      submission,
      taxonomy,
    });

  return EngagementTalentMatchChecklistUtil.hasChecklistItems(
    checklistCategories
  ) ? (
    <Gutter top="16px">
      <Well background="light blue" name="MATCH_CHECKLIST">
        <SectionHeader name="MATCH_CHECKLIST_HEADER">
          How {candidate.calculatedFirstName} meets your legal needs
        </SectionHeader>
        <Gutter top="16px">
          <Layout direction={isSmallScreen ? 'vertical' : 'horizontal'}>
            {checklistCategories
              .filter(category =>
                category.sections.some(section => section.items.length > 0)
              )
              .map((category, categoryIndex, filteredCategories) => {
                const sectionsWithItems = category.sections.filter(
                  section => section.items.length > 0
                );

                const isLastCategory =
                  categoryIndex === filteredCategories.length - 1;

                return (
                  <LayoutItem
                    key={category.name}
                    name="CATEGORY_CONTAINER"
                    rightGutter={
                      !isLastCategory && !isSmallScreen ? '16px' : '0px'
                    }
                    hug
                  >
                    <Well name={category.name} background="light">
                      {sectionsWithItems.map((section, sectionIndex) => {
                        const hasNextSectionWithItems =
                          sectionIndex < sectionsWithItems.length - 1;

                        return (
                          <div key={section.header} data-test={section.name}>
                            <CondensedHeader name="SECTION_HEADER">
                              {section.header}
                            </CondensedHeader>
                            <Gutter top="16px">
                              <MatchChecklist
                                name={`${candidate.id}-${section.header}`}
                              >
                                {section.items.map(
                                  ({ name, icon, children }) => (
                                    <div key={name}>
                                      <MatchChecklistItem
                                        name={name}
                                        icon={icon}
                                      >
                                        {children}
                                      </MatchChecklistItem>
                                      <Gutter bottom="4px" />
                                    </div>
                                  )
                                )}
                              </MatchChecklist>
                            </Gutter>
                            {hasNextSectionWithItems ? (
                              <Gutter bottom="24px" name="SECTION_GUTTER" />
                            ) : null}
                          </div>
                        );
                      })}
                    </Well>
                    {categoryIndex < filteredCategories.length - 1 && (
                      <Gutter
                        only="smallScreen"
                        bottom="16px"
                        name="CATEGORY_GUTTER"
                      />
                    )}
                  </LayoutItem>
                );
              })}
          </Layout>
        </Gutter>
      </Well>
    </Gutter>
  ) : null;
};
